/**
 * On-Call Compensations API Services
 */

import datacentral from '@/utils/axios/datacentral'
import { ErrorResponse, ResponseArray, ResponseObject } from '@/types/shared'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'

import {
    OncallCompensationResponse,
    CompensationReportResponse,
} from '../types/response'

import {
    CompensationFilters,
    MyCompensationsFilters,
    CompensationReportFilters,
    ApproveCompensationPayload,
    RejectCompensationPayload,
    MarkPaidPayload,
    BulkMarkPaidPayload,
} from '../types/payload'

export const getOncallCompensations = async (
    payload: DataGridPayload & CompensationFilters
) => {
    const params = new URLSearchParams({
        page: String(payload?.page || 1),
        page_size: String(payload?.page_size || 10),
        start_date: payload.start_date,
        end_date: payload.end_date,
    })
    if (payload?.q) params.append('q', payload.q)
    if (payload?.filters) params.append('filters', payload.filters)
    if (payload?.employee_id)
        params.append('employee_id', String(payload.employee_id))
    if (payload?.schedule_id)
        params.append('schedule_id', String(payload.schedule_id))
    if (payload?.status) params.append('status', payload.status)

    const response = await datacentral.get<
        ResponseArray<OncallCompensationResponse>
    >(`/oncall/compensations?${params.toString()}`)

    // Transform result to results for GokuDataGrid compatibility
    const data = response.data as any
    if (data.result && !data.results) {
        data.results = data.result
    }
    return data
}

export const getOncallCompensationByID = async (
    id: number
): Promise<ResponseObject<OncallCompensationResponse>> => {
    const response = await datacentral.get<
        ResponseObject<OncallCompensationResponse>
    >(`/oncall/compensations/${id}`)
    return response.data
}

export const getCompensationsByEmployee = async (payload: {
    employeeId: number
    start_date?: string
    end_date?: string
}) => {
    const params = new URLSearchParams()
    if (payload.start_date) params.append('start_date', payload.start_date)
    if (payload.end_date) params.append('end_date', payload.end_date)

    const response = await datacentral.get<
        ResponseArray<OncallCompensationResponse>
    >(
        `/oncall/compensations/employee/${payload.employeeId}?${params.toString()}`
    )
    return response.data
}

export const getMyCompensations = async (payload: MyCompensationsFilters) => {
    const params = new URLSearchParams({
        start_date: payload.start_date,
        end_date: payload.end_date,
    })

    const response = await datacentral.get<
        ResponseArray<OncallCompensationResponse>
    >(`/oncall/compensations/my?${params.toString()}`)

    // Transform result to results for compatibility
    const data = response.data as any
    if (data.result && !data.results) {
        data.results = data.result
    }
    return data
}

export const getCompensationReport = async (
    payload: CompensationReportFilters
): Promise<ResponseObject<CompensationReportResponse>> => {
    const params = new URLSearchParams({
        start_date: payload.start_date,
        end_date: payload.end_date,
    })
    if (payload.department_id)
        params.append('department_id', String(payload.department_id))
    if (payload.schedule_id)
        params.append('schedule_id', String(payload.schedule_id))

    const response = await datacentral.get<
        ResponseObject<CompensationReportResponse>
    >(`/oncall/compensations/report?${params.toString()}`)
    return response.data
}

export const approveOncallCompensation = async (payload: {
    id: number
    data: ApproveCompensationPayload
}): Promise<ResponseObject<OncallCompensationResponse> | ErrorResponse> => {
    const response = await datacentral.put(
        `/oncall/compensations/${payload.id}/approve`,
        payload.data
    )
    return response.data
}

export const rejectOncallCompensation = async (payload: {
    id: number
    data: RejectCompensationPayload
}): Promise<ResponseObject<OncallCompensationResponse> | ErrorResponse> => {
    const response = await datacentral.put(
        `/oncall/compensations/${payload.id}/reject`,
        payload.data
    )
    return response.data
}

export const markCompensationPaid = async (payload: {
    id: number
    data?: MarkPaidPayload
}): Promise<ResponseObject<OncallCompensationResponse> | ErrorResponse> => {
    const response = await datacentral.put(
        `/oncall/compensations/${payload.id}/mark-paid`,
        payload.data
    )
    return response.data
}

export const bulkMarkCompensationsPaid = async (
    payload: BulkMarkPaidPayload
): Promise<ResponseObject<{ count: number }> | ErrorResponse> => {
    const response = await datacentral.put(
        '/oncall/compensations/bulk-paid',
        payload
    )
    return response.data
}
